import React, { useState, useRef } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import './FileUploader.css';

const FileUploader = ({ onFileUploaded, acceptedTypes = "application/pdf,image/*,.zip,.rar", maxSize = 50 * 1024 * 1024 }) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const auth = getAuth();

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = async (file) => {
        // Validate file size
        if (file.size > maxSize) {
            alert(`File size too large. Maximum size is ${Math.round(maxSize / (1024 * 1024))}MB`);
            return;
        }

        // Validate file type
        const fileType = file.type;
        const fileName = file.name.toLowerCase();
        const isValidType = acceptedTypes.split(',').some(type => {
            if (type.includes('*')) {
                return fileType.startsWith(type.replace('*', ''));
            }
            return fileType === type || fileName.endsWith(type.replace('.', ''));
        });

        if (!isValidType) {
            alert('File type not supported. Please upload a PDF, image, or archive file.');
            return;
        }

        setUploading(true);
        setProgress(0);

        try {
            const storage = getStorage();
            const userId = auth.currentUser?.uid;
            const timestamp = Date.now();
            const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
            const storageRef = ref(storage, `sales-products/${userId}/${timestamp}_${sanitizedFileName}`);

            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(Math.round(progress));
                }, 
                (error) => {
                    console.error('Upload error:', error);
                    alert('Upload failed. Please try again.');
                    setUploading(false);
                    setProgress(0);
                }, 
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        onFileUploaded({
                            url: downloadURL,
                            name: file.name,
                            size: file.size,
                            type: file.type
                        });
                        setUploading(false);
                        setProgress(0);
                    } catch (error) {
                        console.error('Error getting download URL:', error);
                        alert('Upload completed but failed to get file URL. Please try again.');
                        setUploading(false);
                        setProgress(0);
                    }
                }
            );
        } catch (error) {
            console.error('Upload error:', error);
            alert('Upload failed. Please try again.');
            setUploading(false);
            setProgress(0);
        }
    };

    const onButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="sales-file-uploader">
            <div 
                className={`sales-upload-zone ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={onButtonClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedTypes}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                />
                
                {uploading ? (
                    <div className="sales-upload-progress">
                        <div className="sales-upload-spinner">
                            <svg width="48" height="48" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="31.416" strokeDashoffset={31.416 - (31.416 * progress) / 100} />
                            </svg>
                            <span className="sales-progress-text">{progress}%</span>
                        </div>
                        <p>Uploading your file...</p>
                        <div className="sales-progress-bar">
                            <div className="sales-progress-fill" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>
                ) : (
                    <div className="sales-upload-content">
                        <div className="sales-upload-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7,10 12,15 17,10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                        </div>
                        <h3>Upload your digital product</h3>
                        <p>Drag and drop your file here, or <span className="sales-upload-link">click to browse</span></p>
                        <div className="sales-upload-info">
                            <span>Supported: PDF, Images, ZIP, RAR</span>
                            <span>Max size: {Math.round(maxSize / (1024 * 1024))}MB</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileUploader;
